"use client";

import HeaderLandingPage from "../components/HeaderLandingPage";
import BottomNavigationBar from "../components/BottomNavigationBar";
import dynamic from 'next/dynamic';

const BlogPostCommentsSection = dynamic(() => import("../components/blog/BlogPostCommentsSection"), { ssr: true });
const WhereToGoNextDesktop = dynamic(() => import("../components/WhereToGoNextDesktop"));
import { FiArrowLeft, FiTag } from "react-icons/fi";
import BlogHeader from "../components/blog/mobile/BlogHeader";
import Image from "next/image";
import { useBlogDetail } from "@/hooks/customer/useBlogDetail";
const BlogDetailsAuthor = dynamic(() => import("../components/blog-detail-page/mobile/BlogDetailsAuthor"), { ssr: true });
const BlogDetailsFAQs = dynamic(() => import("../components/blog-detail-page/mobile/BlogDetailsFAQs"), { ssr: true });
const BlogDetailsRelatedArticles = dynamic(() => import("../components/blog-detail-page/mobile/BlogDetailsRelatedArticles"), { ssr: true });
import BlogContentSection from "../components/blog-detail-page/mobile/BlogContentSection";
const ShareSection = dynamic(() => import("../components/blog-detail-page/desktop/ShareSection"), { ssr: false });
const PopularDealsAndLatestBlogsSections = dynamic(() => import("../components/blog-detail-page/desktop/PopularDealsAndLatestBlogsSections"), { ssr: true });
import Breadcrumb from "../components/Breadcrumb";
import Link from "next/link";
const Footer = dynamic(() => import("../components/desktop/FooterDesktop"), { ssr: true });
const BlogTableOfContents = dynamic(() => import("../components/blog-detail-page/desktop/BlogTableOfContents"), { ssr: true });
import BlogSeoHead from "../components/blog/BlogSeoHead";
import { withCloudinaryDeliveryTransforms } from "@/services/media/cloudinaryUrl";

/**
 * Interactive blog detail shell (client). Article HTML is SSR'd by page.tsx.
 * @param {{
 *   initialBlog?: Record<string, unknown> | null,
 *   pathKey?: string,
 *   serverArticleMode?: boolean,
 *   children?: import("react").ReactNode,
 * }} [props]
 */
export default function BlogDetailPage({
  initialBlog = null,
  pathKey = "",
  serverArticleMode = false,
  children,
} = {}) {
  const {
    getInitials,
    truncateWithEllipsis,
    router,
    loading,
    err,
    blog,
    relatedBlogs,
    latestBlogs,
    isShareOpen,
    setIsShareOpen,
    copyMessage,
    setCopyMessage,
    shareDisplayUrl,
    shareWrapRef,
    shareMobileWrapRef,
    canonicalUrl,
    renderedHtml,
    toc,
    htmlWithIds,
    socialEntries,
    faqs,
    handleCopyUrl,
    handleShareChannel,
    handleBackTwoSteps,
    scrollToTocId,
  } = useBlogDetail({ initialBlog, pathKey });

  const showClientArticle = !serverArticleMode;

  const desktopHeroSrc = blog?.image || blog?.imageMobile || "";
  const mobileHeroSrc = desktopHeroSrc ? withCloudinaryDeliveryTransforms(desktopHeroSrc, { width: 800 }) : "";

  return (
    <>
      <BlogSeoHead
        blog={blog || initialBlog}
        pathKey={pathKey || blog?.slug || blog?.id || ""}
      />

      <div className="min-h-screen bg-page-white">
        <BlogHeader />
        {/* Desktop header */}
        <div className="hidden lg:block">
          <HeaderLandingPage />
          <BottomNavigationBar />
        </div>
        {/* breadcrumb navigation based on the current URL path. */}
        <div className="pt-16 lg:pt-0 hidden lg:block">
          <Breadcrumb />
        </div>
        {loading ? (
          <div className="ta-desktop-home-container py-20 lg:py-32 text-center text-gray-500">
            Loading...
          </div>
        ) : err ? (
          <div className="ta-desktop-home-container py-20 text-center">
            <div className="text-gray-800 font-medium">{err}</div>
            <button
              type="button"
              onClick={handleBackTwoSteps}
              className="mt-3 inline-flex items-center justify-center rounded-lg bg-[#FBBC05] px-4 py-2 btn-text cursor-pointer font-semibold text-black hover:opacity-95"
            >
              Back to blogs
            </button>
          </div>
        ) : (
          <>
            {/* Mobile edge-to-edge hero */}
            <div className="lg:hidden">
              <div className="grid grid-cols-1 grid-rows-1 w-full mt-16 lg:mt-0 bg-gray-900 min-h-[250px]">
                {/* Image */}
                <div className="col-start-1 row-start-1 flex items-center justify-center w-full">
                  {desktopHeroSrc ? (
                    <picture className="w-full h-full flex items-center justify-center">
                      <source media="(min-width: 1024px)" srcSet="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" />
                      {mobileHeroSrc && (
                        <source media="(max-width: 1023px)" srcSet={mobileHeroSrc} />
                      )}
                      <img 
                        src={desktopHeroSrc} 
                        alt={blog?.title || "Blog image"} 
                        className="w-full h-auto object-contain block" 
                        style={{ width: '100%', height: 'auto', objectFit: 'contain' }} 
                      />
                    </picture>
                  ) : null}
                </div>
                
                {/* Dark overlay */}
                <div className="col-start-1 row-start-1 bg-black/15 z-10 w-full h-full" />
                
                {/* breadcrumb - image ke upar overlay */}
                <div className="col-start-1 row-start-1 z-30 w-full">
                  <Breadcrumb transparent />
                </div>

                {/* Title and content */}
                <div className="col-start-1 row-start-1 z-20 flex items-end h-full ta-desktop-home-container lg:max-w-none lg:mx-0 lg:px-0 w-full">
                  <div className="w-full pb-6 pt-20 text-white px-5 lg:px-0">
                    {blog?.category ? (
                      <span className="mb-3 inline-block bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-semibold text-black shadow-sm">
                        {blog.category}
                      </span>
                    ) : null}
                    {showClientArticle ? (
                      <h1 className="leading-[1.15] font-extrabold line-clamp-3">
                        {blog?.title}
                      </h1>
                    ) : (
                      <p className="leading-[1.15] font-extrabold line-clamp-3">
                        {blog?.title}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Author + writer social links (below hero); share sheet via icon */}
              <BlogDetailsAuthor
                getInitials={getInitials}
                blog={blog}
                setIsShareOpen={setIsShareOpen}
                setCopyMessage={setCopyMessage}
                socialEntries={socialEntries}
                isShareOpen={isShareOpen}
                shareMobileWrapRef={shareMobileWrapRef}
                canonicalUrl={canonicalUrl}
                shareDisplayUrl={shareDisplayUrl}
                handleCopyUrl={handleCopyUrl}
                handleShareChannel={handleShareChannel}
                copyMessage={copyMessage}
              />
            </div>

            <div className="ta-desktop-home-container pt-6 lg:pt-0">
               <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-x-6 gap-y-0 items-start">
                  
                  {/* LEFT COLUMN: Desktop Hero + Mobile Tags + Shared Article */}
                  <div className="min-w-0">
                      
                      {/* Mobile tags & excerpt */}
                      <div className="lg:hidden pb-3 space-y-6">
                        {blog?.tags?.length ? (
                          <div className="flex flex-wrap gap-2">
                            {blog.tags.map((tag, idx) => (
                              <span
                                key={`${tag}-${idx}`}
                                className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded-full span-text"
                              >
                                <FiTag className="w-3 h-3" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {blog?.excerpt ? (
                          <p className="text-sm text-gray-700">{blog.excerpt}</p>
                        ) : null}
                      </div>

                      {/* Desktop Hero */}
                      <div className="hidden lg:block">
                        <div className="min-w-0 max-w-3xl">
                          {showClientArticle ? (
                            <h1 className="leading-[1.15] font-semibold text-black">
                              {blog?.title}
                            </h1>
                          ) : (
                            <div className="min-w-0 flex-1" aria-hidden />
                          )}
                        </div>
                        
                        <div className="min-w-0 mt-6 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 shrink-0 rounded-full bg-[#EDEDED] flex items-center justify-center text-sm font-semibold text-black">
                              {getInitials(blog?.author)}
                            </div>
                            <div className="text-sm text-gray-700 min-w-0">
                              <div className="font-medium text-black truncate">
                                {blog?.author}
                              </div>
                              <div className="text-[11px] text-gray-500 leading-snug">
                                Published date: {blog?.date || "—"}
                                {blog?.updatedDate ? (
                                  <>
                                    <br />
                                    Updated date: {blog.updatedDate}
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <ShareSection
                              shareWrapRef={shareWrapRef}
                              setIsShareOpen={setIsShareOpen}
                              setCopyMessage={setCopyMessage}
                              isShareOpen={isShareOpen}
                              canonicalUrl={canonicalUrl}
                              shareDisplayUrl={shareDisplayUrl}
                              handleCopyUrl={handleCopyUrl}
                              handleShareChannel={handleShareChannel}
                              copyMessage={copyMessage}
                            />
                            {socialEntries.length ? (
                              <div
                                className="flex items-center gap-2"
                                aria-label="Writer social links"
                              >
                                {socialEntries.map((s) => (
                                  <Link
                                    key={s.key}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={s.label}
                                    aria-label={s.label}
                                    className="h-7 w-7 rounded-full bg-[#1E2B55] text-white flex items-center justify-center hover:opacity-90"
                                  >
                                    <s.Icon
                                      className="h-3.5 w-3.5"
                                      aria-hidden="true"
                                    />
                                  </Link>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                <div className="min-w-0  flex items-center">
                          {/* <h2
                            id="blog-detail-popular-deals"
                            className="text-black font-medium"
                          >
                            Popular deals
                          </h2> */}
                </div>

                        {desktopHeroSrc ? (
                          <div className="mt-4 w-full flex justify-center">
                            <div className="relative w-full rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                              <picture className="w-full h-full block">
                                <source media="(max-width: 1023px)" srcSet="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" />
                                <img
                                  src={desktopHeroSrc}
                                  alt={blog?.title}
                                  className="w-full h-auto object-contain block"
                                  style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                                />
                              </picture>
                              {blog?.category ? (
                                <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-semibold text-black shadow-sm">
                                  {blog.category}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        {toc.length ? (
                          <BlogTableOfContents toc={toc} scrollToTocId={scrollToTocId} truncateWithEllipsis={truncateWithEllipsis} />
                        ) : null}
                      </div>

                      {/* SHARED ARTICLE SLOT */}
                      <div className="w-full mt-4 lg:mt-8">
                        {serverArticleMode ? (
                          children
                        ) : (
                          <div className="w-full">
                            <BlogContentSection htmlWithIds={htmlWithIds} renderedHtml={renderedHtml} />
                          </div>
                        )}
                      </div>
                  </div>
                  
                  {/* RIGHT COLUMN: Sidebar */}
                  <aside className="hidden lg:block min-w-0 mt-4 self-start row-span-3 pb-[90vh] sticky top-24">
                    <PopularDealsAndLatestBlogsSections
                      router={router}
                      latestBlogs={latestBlogs}
                      hidePopularHeading
                    />
                  </aside>
               </div>
            </div>
          </>
        )}

        {/* Shared section for FAQ, Comments, and Related Articles (rendered once) */}
        <div className="ta-desktop-home-container mt-8 lg:mt-12 space-y-10 lg:space-y-14">
          {/* FAQ */}
          <div className="w-full">
            <BlogDetailsFAQs faqs={faqs} />
          </div>

          {/* Comments */}
          <div className="w-full">
            <BlogPostCommentsSection blogPublicId={blog?.id} />
          </div>

          {/* Related Articles */}
          {relatedBlogs.length ? (
            <div className="w-full">
              <BlogDetailsRelatedArticles
                relatedBlogs={relatedBlogs}
                router={router}
              />
            </div>
          ) : null}
        </div>

        <div className="pt-3 pb-6">
          <WhereToGoNextDesktop />
        </div>
        <Footer />
      </div>
    </>
  );
}
